/**
 * @file Leave request modal owner for the Shared Calendar domain.
 */

import { closeForm } from "../../shared/forms/modalShell.js";
import { openStandardForm } from "../../shared/forms/standardFormModal.js";
import {
    bindNativeFieldBehavior,
    FIXED_TEXTAREA_CLASSES,
    getNativeFieldWrapperHtml,
    READ_ONLY_VALUE_CLASSES,
} from "../../shared/forms/nativeFieldStyles.js";
import { createDateControl } from "../../shared/ui/controls/dateControl.js";
import { getRegisteredControlValues } from "../../shared/ui/controls/controlCore.js";
import { openConfirmationModal } from "../../shared/ui/confirmationModal.js";
import { createSelectControl } from "../../shared/ui/controls/selectControl.js";
import { createUserOptions } from "../../shared/ui/controls/controlOptions.js";
import { escapeHTML } from "../../shared/utils/contentUtils.js";
import {
    isDrugsAndLeaveActionLocked,
} from "../../shared/utils/actionLock.js";
import { db } from "../../app/firebase/firebase.js";
import { appData, state } from "../../app/state/state.js";
import {
    doc,
    getDoc,
} from "https://www.gstatic.com/firebasejs/12.19.0/firebase-firestore.js";
import {
    previewLeaveRequestOverlaps,
    requestLeaveEdit,
    submitLeaveRequest,
} from "./calendarApi.js";
import { getCalendarDateKey } from "./calendarDateUtils.js";
import { formatLeaveOverlapDateLabel } from "./leaveDetailFormatting.js";
import {
    showCalendarConfirmationModal,
    showCalendarErrorModal,
} from "./calendarModalFeedback.js";
import {
    bindCalendarItemThresholdControl,
    CALENDAR_THRESHOLD_ITEM_TYPES,
    getCalendarItemThresholdDraft,
    readCalendarItemThresholds,
    renderCalendarItemThresholdControl,
} from "./notificationThresholdControls.js";

const DATE_RANGE_ERROR_MESSAGE = "End date cannot be before start date.";
const LEAVE_SUBMITTED_TITLE = "Leave Submitted";
const LEAVE_SUBMITTED_MESSAGE = "Leave submitted successfully.";
const ACTION_LOCK_MESSAGE = "Leave actions are temporarily locked for maintenance.";

function getTodayDateKey() {
    return getCalendarDateKey(new Date());
}

function getCurrentUserId() {
    return state.currentUser?.uid || state.currentUserProfile?.id || "";
}

function userHasCalendarAccess(user) {
    return user?.role === "admin" ||
        (Array.isArray(user?.permissions) && user.permissions.includes("calendar"));
}

function canManageLeaveForOthers() {
    const detailTeamId = appData.config?.leave?.detailTeamId || "";
    const currentUser = state.currentUserProfile || {};
    const currentUserId = getCurrentUserId();
    const isTeamDocMember = (appData.teams || []).some((team) =>
        team?.id === detailTeamId &&
        Array.isArray(team.members) &&
        team.members.includes(currentUserId));
    return Boolean(
        detailTeamId &&
        currentUser.isActive !== false &&
        userHasCalendarAccess(currentUser) &&
        isTeamDocMember,
    );
}

function getSelectableLeaveUsers() {
    const currentUserId = getCurrentUserId();
    return (appData.users || [])
        .filter((user) => user?.id && user.id !== currentUserId)
        .filter((user) => user.isActive !== false)
        .filter(userHasCalendarAccess);
}

function getUserName(userId) {
    const user = (appData.users || []).find((entry) => entry.id === userId);
    return user?.name || user?.email || userId || "Unknown user";
}

function getLeaveTypes() {
    return (appData.config?.leave?.types || [])
        .filter((type) => type?.id && type.isActive !== false)
        .sort((a, b) => Number(a.order || 0) - Number(b.order || 0));
}

function getDefaultLeaveTypeId(leaveTypes) {
    const defaultTypeId = appData.config?.leave?.defaultTypeId || "";
    return leaveTypes.some((type) => type.id === defaultTypeId) ?
        defaultTypeId :
        leaveTypes[0]?.id || "";
}

function getInitialLeaveData(leave = null) {
    const today = getTodayDateKey();
    const leaveTypes = getLeaveTypes();
    const defaultTypeId = getDefaultLeaveTypeId(leaveTypes);
    const data = {
        id: leave?.id || leave?.sourceId || "",
        startDate: leave?.startDate || today,
        endDate: leave?.endDate || leave?.startDate || today,
        leaveTypeId: leave?.leaveTypeId || defaultTypeId,
        details: leave?.details || "",
        requesterId: leave?.requesterId || "",
        requesterName: leave?.requesterName || "",
    };
    data.notificationThreshold = getCalendarItemThresholdDraft({
        itemType: CALENDAR_THRESHOLD_ITEM_TYPES.LEAVE,
        item: leave,
        config: appData.config || {},
    });
    return data;
}

async function resolveLeaveFormSource(leave) {
    const sourceId = leave?.sourceType === "leave" ?
        leave?.sourceId || leave?.id || "" :
        "";
    if (!sourceId || Array.isArray(leave?.notificationThresholds)) {
        return leave;
    }

    try {
        const snapshot = await getDoc(doc(db, "leave_requests", sourceId));
        if (!snapshot.exists()) {return leave;}
        return { id: snapshot.id, ...snapshot.data() };
    } catch (error) {
        console.error("Error loading Leave request for edit:", error);
        return leave;
    }
}

function getLeaveTypeOptions() {
    return getLeaveTypes().map((type) => ({
        value: type.id,
        label: type.name || type.id,
    }));
}

function getTargetRequesterHtml(context = {}) {
    if (context.fixedTargetRequesterId) {
        const requesterName = context.fixedTargetRequesterName ||
            getUserName(context.fixedTargetRequesterId);
        return getNativeFieldWrapperHtml("User", `
            <div class="${READ_ONLY_VALUE_CLASSES}">
                ${escapeHTML(requesterName)}
            </div>
        `, { className: "md:col-span-2" });
    }

    if (!context.canManageForOthers) {return "";}
    return `
        <div class="min-w-0 space-y-1 md:col-span-2 ${context.leaveForOthers ? "" : "hidden"}" data-calendar-leave-target-wrapper>
            <label class="block text-base text-gray-500 sm:text-sm">
                User<span class="ml-1 font-semibold text-red-600" aria-label="required">*</span>
            </label>
            <div data-calendar-leave-user-control></div>
        </div>
    `;
}

function getLeaveFieldsHtml(rawLeave, context = {}) {
    const leave = getInitialLeaveData(rawLeave);
    const disabledAttr = context.actionLocked ? " disabled" : "";
    return `
        ${context.actionLocked ? `
            <p class="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                ${escapeHTML(ACTION_LOCK_MESSAGE)}
            </p>
        ` : ""}
        <div class="grid grid-cols-1 gap-4 md:grid-cols-2" data-calendar-leave-fields>
            ${getTargetRequesterHtml(context)}
            <div data-calendar-leave-date-control="startDate"></div>
            <div data-calendar-leave-date-control="endDate"></div>
            ${getNativeFieldWrapperHtml(
                "Leave Type",
                "<div data-calendar-leave-type-control></div>",
                { className: "md:col-span-2", required: true },
            )}
            ${getNativeFieldWrapperHtml("Details", `
                <textarea
                    name="details"
                    rows="4"
                    class="${FIXED_TEXTAREA_CLASSES}"
                    data-calendar-leave-input
                    ${disabledAttr}
                >${escapeHTML(leave.details || "")}</textarea>
            `, { className: "md:col-span-2" })}
            <div class="md:col-span-2">
                ${renderCalendarItemThresholdControl({
                    itemType: CALENDAR_THRESHOLD_ITEM_TYPES.LEAVE,
                    threshold: leave.notificationThreshold,
                    label: "Leave Reminders",
                    disabled: context.actionLocked === true,
                    readOnlyAdminDefaults: true,
                })}
            </div>
        </div>
    `;
}

function mountLeaveControls(itemForm, leaveData, context = {}) {
    const syncDateRange = () => validateLeaveDateRange(itemForm, { showError: true });
    const startDateRoot = itemForm.querySelector("[data-calendar-leave-date-control=\"startDate\"]");
    const endDateRoot = itemForm.querySelector("[data-calendar-leave-date-control=\"endDate\"]");
    const leaveTypeRoot = itemForm.querySelector("[data-calendar-leave-type-control]");
    const requesterRoot = itemForm.querySelector("[data-calendar-leave-user-control]");

    if (requesterRoot) {
        createSelectControl({
            root: requesterRoot,
            name: "targetRequesterId",
            value: context.targetRequesterId || "",
            options: createUserOptions(getSelectableLeaveUsers()),
            placeholder: "Select user",
            accessibleName: "Leave for user",
            searchable: true,
            searchPlaceholder: "Search users",
            searchThreshold: 7,
            portalMenu: true,
            menuBoundary: "#form-modal",
            disabled: context.actionLocked,
        });
    }

    if (startDateRoot) {
        createDateControl({
            root: startDateRoot,
            name: "startDate",
            nativeInputName: "startDate",
            label: "First day of leave",
            value: leaveData.startDate,
            required: true,
            requiredMessage: "First day of leave is required.",
            onChange: syncDateRange,
            disabled: context.actionLocked,
        });
    }

    if (endDateRoot) {
        createDateControl({
            root: endDateRoot,
            name: "endDate",
            nativeInputName: "endDate",
            label: "Last day of leave",
            value: leaveData.endDate,
            required: true,
            requiredMessage: "Last day of leave is required.",
            onChange: syncDateRange,
            disabled: context.actionLocked,
        });
    }

    if (leaveTypeRoot) {
        createSelectControl({
            root: leaveTypeRoot,
            name: "leaveTypeId",
            value: leaveData.leaveTypeId,
            options: getLeaveTypeOptions(),
            required: true,
            requiredMessage: "Leave type is required.",
            placeholder: "Select leave type",
            accessibleName: "Leave Type",
            portalMenu: true,
            menuBoundary: "#form-modal",
            disabled: context.actionLocked,
        });
    }

    syncDateRange();
}

function collectLeaveData(itemForm, controlValues = {}, context = {}) {
    const formData = new FormData(itemForm);
    const payload = {
        startDate: String(controlValues.startDate || ""),
        endDate: String(controlValues.endDate || ""),
        leaveTypeId: String(controlValues.leaveTypeId || ""),
        details: String(formData.get("details") || "").trim() || null,
        notificationThresholds: readCalendarItemThresholds(itemForm, {
            itemType: CALENDAR_THRESHOLD_ITEM_TYPES.LEAVE,
        }),
    };
    if (context.fixedTargetRequesterId) {
        payload.targetRequesterId = context.fixedTargetRequesterId;
    } else if (context.leaveForOthers) {
        payload.targetRequesterId = String(controlValues.targetRequesterId || "");
    }
    return payload;
}

function setSubmitting(itemForm, isSubmitting) {
    const submitButton = itemForm.querySelector("button[type=\"submit\"]");
    if (!submitButton) {return;}
    submitButton.disabled = isSubmitting;
    submitButton.classList.toggle("opacity-60", isSubmitting);
    submitButton.textContent = isSubmitting ? "Submitting..." : "Submit";
}

function renderOverlapWarningHtml(warnings = []) {
    return `
        <div class="space-y-3 text-left">
            <p class="text-sm text-gray-600">
                This leave overlaps approved leave for:
            </p>
            <ul class="space-y-2 text-sm text-gray-700">
                ${warnings.map((warning) => `
                    <li class="rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
                        <strong>${escapeHTML(warning.userName || "Unknown user")}</strong>
                        <span class="block text-xs text-gray-500">
                            ${escapeHTML(formatLeaveOverlapDateLabel(warning))}
                        </span>
                    </li>
                `).join("")}
            </ul>
        </div>
    `;
}

function renderDateRangeError(itemForm, message) {
    const errorContainer = itemForm.querySelector("#form-fields") || itemForm;
    errorContainer
        .querySelectorAll("[data-calendar-leave-date-error]")
        .forEach((element) => element.remove());
    errorContainer.insertAdjacentHTML("afterbegin", `
        <p class="mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-calendar-leave-date-error>
            ${escapeHTML(message)}
        </p>
    `);
}

function renderLeaveError(itemForm, message) {
    const errorContainer = itemForm.querySelector("#form-fields") || itemForm;
    errorContainer
        .querySelectorAll("[data-calendar-leave-error]")
        .forEach((element) => element.remove());
    errorContainer.insertAdjacentHTML("afterbegin", `
        <p class="mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-calendar-leave-error>
            ${escapeHTML(message)}
        </p>
    `);
}

function clearLeaveError(itemForm) {
    itemForm
        .querySelectorAll("[data-calendar-leave-error]")
        .forEach((element) => element.remove());
}

function clearDateRangeError(itemForm) {
    itemForm
        .querySelectorAll("[data-calendar-leave-date-error]")
        .forEach((element) => element.remove());
}

function validateLeaveDateRange(itemForm, { showError = false } = {}) {
    const startInput = itemForm.querySelector("input[type=\"date\"][name=\"startDate\"]");
    const endInput = itemForm.querySelector("input[type=\"date\"][name=\"endDate\"]");
    const isInvalid = Boolean(
        startInput?.value &&
        endInput?.value &&
        endInput.value < startInput.value,
    );

    endInput?.setCustomValidity(isInvalid ? DATE_RANGE_ERROR_MESSAGE : "");

    if (isInvalid && showError) {
        renderDateRangeError(itemForm, DATE_RANGE_ERROR_MESSAGE);
    } else if (!isInvalid) {
        clearDateRangeError(itemForm);
    }

    return !isInvalid;
}

function bindLeaveDateValidation(itemForm) {
    const startInput = itemForm.querySelector("input[type=\"date\"][name=\"startDate\"]");
    const endInput = itemForm.querySelector("input[type=\"date\"][name=\"endDate\"]");
    const sync = () => validateLeaveDateRange(itemForm, { showError: true });

    startInput?.addEventListener("input", sync);
    startInput?.addEventListener("change", sync);
    endInput?.addEventListener("input", sync);
    endInput?.addEventListener("change", sync);
    sync();
}

function validateTargetRequester(itemForm, controlValues = {}, context = {}) {
    if (!context.leaveForOthers || context.fixedTargetRequesterId) {
        clearLeaveError(itemForm);
        return true;
    }
    if (String(controlValues.targetRequesterId || "")) {
        clearLeaveError(itemForm);
        return true;
    }
    renderLeaveError(itemForm, "Select the user this leave is for.");
    return false;
}

function configureLeaveForOthersToggle({ itemForm, context }) {
    const formHeaderActions = document.querySelector("#form-header-actions");
    if (!formHeaderActions || !context.canManageForOthers || context.fixedTargetRequesterId) {
        return;
    }

    formHeaderActions.innerHTML = `
        <label class="flex items-center gap-2 text-sm font-medium text-gray-700">
            <span>Leave for others</span>
            <span class="toggle-switch shrink-0">
                <input
                    type="checkbox"
                    id="calendar-leave-for-others-toggle"
                    form="item-form"
                    data-calendar-leave-for-others-toggle
                    ${context.leaveForOthers ? "checked" : ""}
                    ${context.actionLocked ? "disabled" : ""}
                >
                <span class="slider"></span>
            </span>
        </label>
    `;

    bindNativeFieldBehavior(formHeaderActions);
    const targetWrapper = itemForm.querySelector("[data-calendar-leave-target-wrapper]");
    const toggle = formHeaderActions.querySelector("[data-calendar-leave-for-others-toggle]");
    toggle?.addEventListener("change", () => {
        context.leaveForOthers = toggle.checked;
        targetWrapper?.classList.toggle("hidden", !context.leaveForOthers);
        clearLeaveError(itemForm);
    });
}

function applyLeaveActionLock(itemForm) {
    const submitButton = itemForm.querySelector("button[type=\"submit\"]");
    if (!submitButton) {return;}
    submitButton.disabled = true;
    submitButton.title = ACTION_LOCK_MESSAGE;
    submitButton.classList.add("opacity-60", "cursor-not-allowed");
}

function showLeaveSubmittedModal() {
    showCalendarConfirmationModal(
        LEAVE_SUBMITTED_TITLE,
        LEAVE_SUBMITTED_MESSAGE,
    );
}

function queueLeaveSubmittedModal() {
    window.setTimeout(showLeaveSubmittedModal, 0);
}

function showLeaveRequestErrorModal(error) {
    showCalendarErrorModal(
        "Leave Request Failed",
        error,
        "Leave request could not be submitted.",
    );
}

function queueLeaveRequestErrorModal(error) {
    window.setTimeout(() => showLeaveRequestErrorModal(error), 0);
}

async function submitLeaveForm({
    deferConfirmation = false,
    existingLeave,
    leaveData,
}) {
    if (existingLeave?.id) {
        await requestLeaveEdit(existingLeave.id, leaveData);
    } else {
        await submitLeaveRequest(leaveData);
    }
    closeForm();
    if (deferConfirmation) {
        queueLeaveSubmittedModal();
    } else {
        showLeaveSubmittedModal();
    }
}

/**
 * Opens the leave request modal.
 * @param {object|null} [leave=null] Approved leave to edit.
 * @returns {void}
 */
export async function openLeaveRequestModal(leave = null) {
    const sourceLeave = await resolveLeaveFormSource(leave);
    const leaveData = getInitialLeaveData(sourceLeave);
    const currentUserId = getCurrentUserId();
    const canManageForOthers = canManageLeaveForOthers();
    const actionLocked = isDrugsAndLeaveActionLocked();
    const fixedTargetRequesterId = leaveData.id &&
        leaveData.requesterId &&
        leaveData.requesterId !== currentUserId &&
        canManageForOthers ?
        leaveData.requesterId :
        "";
    const context = {
        canManageForOthers,
        fixedTargetRequesterId,
        fixedTargetRequesterName: leaveData.requesterName ||
            getUserName(fixedTargetRequesterId),
        leaveForOthers: false,
        targetRequesterId: fixedTargetRequesterId,
        actionLocked,
    };

    openStandardForm("calendar_leave", leaveData.id ? leaveData : null, {
        getFieldsHtml: () => getLeaveFieldsHtml(sourceLeave, context),
        afterRender({ itemForm }) {
            const formTitle = document.querySelector("#form-title");
            if (formTitle) {
                formTitle.textContent = leaveData.id ?
                    (fixedTargetRequesterId ? "Edit Leave" : "Request Leave Edit") :
                    "Request Leave";
            }
            configureLeaveForOthersToggle({ itemForm, context });
            mountLeaveControls(itemForm, leaveData, context);
            bindCalendarItemThresholdControl(itemForm, {
                itemType: CALENDAR_THRESHOLD_ITEM_TYPES.LEAVE,
                readOnlyAdminDefaults: true,
            });
            bindNativeFieldBehavior(itemForm);
            bindLeaveDateValidation(itemForm);
            if (actionLocked) {
                applyLeaveActionLock(itemForm);
            }
            itemForm.onsubmit = async (event) => {
                event.preventDefault();
                if (actionLocked) {
                    renderLeaveError(itemForm, ACTION_LOCK_MESSAGE);
                    return;
                }
                const controlExtraction = getRegisteredControlValues(itemForm, {
                    validate: true,
                });
                if (
                    !controlExtraction.valid ||
                    !validateTargetRequester(
                        itemForm,
                        controlExtraction.values,
                        context,
                    ) ||
                    !validateLeaveDateRange(itemForm, { showError: true }) ||
                    !itemForm.reportValidity()
                ) {return;}

                const payload = collectLeaveData(
                    itemForm,
                    controlExtraction.values,
                    context,
                );
                try {
                    setSubmitting(itemForm, true);
                    const preview = await previewLeaveRequestOverlaps(
                        payload,
                        leaveData.id || null,
                    );
                    const warnings = preview?.overlapWarnings || [];
                    if (warnings.length > 0) {
                        openConfirmationModal(
                            "Overlapping Leave",
                            renderOverlapWarningHtml(warnings),
                            async () => {
                                try {
                                    await submitLeaveForm({
                                        deferConfirmation: true,
                                        leaveData: payload,
                                        existingLeave: leaveData.id ? leaveData : null,
                                    });
                                } catch (error) {
                                    console.error(
                                        "Error submitting leave request:",
                                        error,
                                    );
                                    queueLeaveRequestErrorModal(error);
                                }
                            },
                            true,
                            "Submit Anyway",
                            null,
                            "Submitting...",
                            "Review",
                        );
                        return;
                    }
                    await submitLeaveForm({
                        leaveData: payload,
                        existingLeave: leaveData.id ? leaveData : null,
                    });
                } catch (error) {
                    console.error("Error submitting leave request:", error);
                    showLeaveRequestErrorModal(error);
                } finally {
                    setSubmitting(itemForm, false);
                }
            };
        },
    });
}
